import { Button, useMultiStepForm, V2Loader, cn } from "web-design-system";
import {
  useInvestorPrimaryBankAccount,
  useNotifyInvestorMutation,
  useSendKycEmail,
} from "creso-core/hooks";
import { toast } from "sonner";
import {
  SuccessLottieAnimation,
  WarningLottieAnimation,
  useClientOnboarding,
} from "web-core/components";
import { BankIcon } from "@core/components/icon/bank-icon";
import { CopyableText } from "@core/components/copyable-text";
import { handleError } from "@/utils/error";
import { IdCard } from "lucide-react";

export function NotifyClient() {
  const { client, close } = useClientOnboarding();
  const { goToStep } = useMultiStepForm();

  const { data: bankDetails, isLoading } = useInvestorPrimaryBankAccount(
    client?.investment_account ?? ""
  );

  const { mutateAsync: sendKycEmail, isPending } = useSendKycEmail();

  const { mutate: notifyInvestor, isPending: isNotifyingInvestor } =
    useNotifyInvestorMutation();

  const handleNotifyInvestor = () => {
    notifyInvestor(client?.investment_account ?? "", {
      onSuccess: () => {
        toast.success("Client notified successfully");
        close();
      },
    });
  };

  const isOnboardingComplete =
    bankDetails?.verification_status === "verified" &&
    client?.kyc_status === "S";

  return (
    <V2Loader
      isLoading={isLoading}
      className="flex flex-col items-center justify-center p-5 h-full"
    >
      <div className="flex-1 flex flex-col items-center justify-start size-full">
        <div className="flex items-center gap-4 flex-col justify-center mt-10">
          {isOnboardingComplete ? (
            <>
              <SuccessLottieAnimation loop={false} height={100} width={100} />
              <h4>Setup complete</h4>
            </>
          ) : (
            <>
              <WarningLottieAnimation loop={false} height={100} width={100} />
              <h4>Setup incomplete</h4>
            </>
          )}
        </div>
        <div className={cn("h-px bg-border w-full my-4")} />
        {isOnboardingComplete && (
          <div className="flex flex-col text-sm space-y-1 px-10">
            <h6 className="mb-2">Next steps. Ask {client?.name} to:</h6>
            <span>
              1. Login to creso app
              <CopyableText
                className="underline text-sm"
                label="Share app link"
                text="https://4bmjx.app.link/creso-app"
              />
            </span>
            <span>2. Verify their details</span>
            <span>3. Authenticate with OTP, and complete account creation</span>
          </div>
        )}
        {!isOnboardingComplete && (
          <div className="flex flex-col text-sm gap-4 w-full">
            <h6 className="text-sm font-medium">Pending items</h6>
            {client?.kyc_status === "F" && (
              <div className="flex flex-col gap-2 w-full bg-tag-orange rounded-lg p-3">
                <div className="flex font-semibold flex-row w-full gap-2 items-center">
                  <IdCard className="size-4 text-tag-orange-foreground" />
                  <span className="text-sm tracking-tight font-semibold text-tag-orange-foreground">
                    KYC pending
                  </span>
                  <Button
                    size="sm"
                    className="ml-auto font-semibold h-auto p-0 shrink-0"
                    variant="link"
                    disabled={isPending}
                    onClick={() =>
                      sendKycEmail(client.investment_account ?? "").catch(
                        (error) => handleError(error, "Failed to send KYC link")
                      )
                    }
                  >
                    Notify client
                  </Button>
                </div>
                <span className="text-sm leading-5 text-muted-foreground">
                  Your client needs to complete KYC process, notify them with
                  instructions.
                </span>
              </div>
            )}
            {bankDetails?.verification_status !== "verified" && (
              <div className="flex flex-col gap-2 w-full bg-tag-orange rounded-lg p-3">
                <div className="flex font-semibold flex-row w-full gap-2 items-center">
                  <BankIcon className="stroke-tag-orange-foreground" />
                  <span className="text-sm tracking-tight font-semibold text-tag-orange-foreground">
                    Bank details
                  </span>
                  <Button
                    size="sm"
                    className="ml-auto font-semibold h-auto p-0 shrink-0"
                    variant="link"
                    onClick={() => goToStep(2)}
                  >
                    Edit
                  </Button>
                </div>
                <span className="text-sm leading-5 text-muted-foreground">
                  {bankDetails?.verification_status === "unverified"
                    ? "We couldn't verify the account at this point, please try again later."
                    : (bankDetails?.verification_remark ??
                      "Bank account verification failed.")}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      {isOnboardingComplete && (
        <Button
          onClick={handleNotifyInvestor}
          disabled={isNotifyingInvestor}
          className="w-full"
          size="lg"
        >
          Notify client
        </Button>
      )}
      {bankDetails?.verification_status === "unverified" && (
        <Button onClick={close} className="w-full" size="lg">
          Close
        </Button>
      )}
    </V2Loader>
  );
}
